import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "./axiosBaseQuery";
import { normalizeSelectionIds } from "@/lib/utils/selection-ids.utils";

/**
 * Saved Companies API.
 *
 * Talks to the real Express middleware at `/api/saved-companies/*`. The
 * middleware resolves the user from the session cookie and forwards to the
 * Saved Companies service, so no user id is sent from the browser.
 */

interface Envelope<T> {
  success: boolean;
  message?: string;
  data: T;
}

/**
 * One saved-company hit as the upstream service actually returns it - the same shape as the
 * live company search API (see companyApi.ts's CompanySearchHit). Confirmed live (2026-09-07)
 * to include a real `company_id` now; deriveSavedCompanyId() below still falls back to
 * website/linkedin/name for older records or deployments that don't carry it, mirroring
 * companyApi.ts's deriveCompanyId.
 */
export interface SavedCompanyRecord {
  id?: string;
  _id?: string;
  companyId?: string;
  company_id?: string;
  name?: string;
  company_name?: string;
  website?: string;
  industry?: string | string[];
  // UK-specific classification carried by uk_data companies - the Industry
  // fallback when `industry` is empty.
  uk_industry?: string;
  linkedin_link?: string;
  linkedin_link_by_number?: string;
  city?: string;
  state?: string;
  country?: string;
  comp_size_range?: string;
  size_by_number?: number;
  dateAdded?: string;
  [key: string]: unknown;
}

export interface SavedCompaniesSearchResponse {
  total: number;
  results: SavedCompanyRecord[];
}

const EMPTY_SEARCH: SavedCompaniesSearchResponse = { total: 0, results: [] };

export const savedCompaniesApi = createApi({
  reducerPath: "savedCompaniesApi",
  baseQuery: axiosBaseQuery("/api/saved-companies"),
  tagTypes: ["SavedCompanies"],
  endpoints: (builder) => ({
    saveCompanies: builder.mutation<unknown, { companyIds: string[] }>({
      query: ({ companyIds }) => ({
        url: "/save",
        method: "POST",
        body: { companyIds },
      }),
      invalidatesTags: ["SavedCompanies"],
    }),

    deleteSavedCompanies: builder.mutation<unknown, { companyIds: string[] }>({
      query: ({ companyIds }) => ({
        url: "/",
        method: "DELETE",
        body: { companyIds },
      }),
      invalidatesTags: ["SavedCompanies"],
    }),

    searchSavedCompanies: builder.query<
      SavedCompaniesSearchResponse,
      { from?: number; size?: number; body?: unknown } | void
    >({
      query: (args) => {
        const { from = 0, size = 10, body = {} } = args ?? {};
        return {
          url: "/search",
          method: "POST",
          body,
          params: { from, size },
        };
      },
      transformResponse: (response: Envelope<any>) => {
        const data = response?.data ?? {};
        const results = data.results ?? data.companies ?? data.savedCompanies ?? [];
        return {
          total: typeof data.total === "number" ? data.total : results.length,
          results: Array.isArray(results) ? results : [],
        };
      },
      providesTags: ["SavedCompanies"],
    }),

    /**
     * Ids-only counterpart of searchSavedCompanies, for the bulk action bar's
     * "Select N". Same filter body and paging, but the upstream projects only
     * the key field and answers { total, from, size, ids }.
     *
     * Lets the Saved tab select past the slice it holds in memory: selection
     * used to be served entirely from the client-side list, which is fetched
     * capped at 1000 rows.
     */
    searchSavedCompaniesSelection: builder.query<
      string[],
      { from?: number; size?: number; body?: unknown } | void
    >({
      query: (args) => {
        const { from = 0, size = 25, body = {} } = args ?? {};
        return {
          url: "/search/selection",
          method: "POST",
          body,
          params: { from, size },
        };
      },
      transformResponse: (response: Envelope<unknown>) => normalizeSelectionIds(response),
      // Deliberately untagged - see the note on searchSavedContactsSelection.
      keepUnusedDataFor: 60,
    }),

    /**
     * Queues the selected saved companies for a push into the connected CRM.
     * The push runs in the background on the CRM service, so this only
     * confirms it started.
     */
    exportSavedCompaniesToCrm: builder.mutation<
      { batchId?: string; status?: string },
      {
        crmType: "hubspot" | "salesforce";
        companyIds: string[];
        /** HubSpot list id; Salesforce accounts have nothing to enroll into. */
        segmentId?: string;
      }
    >({
      query: (body) => ({
        url: "/export-crm",
        method: "POST",
        body,
      }),
      transformResponse: (response: Envelope<{ batchId?: string; status?: string }>) => response.data,
    }),

    /**
     * All-tab export: saves the given live-search companies (they need not be
     * saved yet) and emails the CSV. Queued on the Search Service - this only
     * confirms it was accepted, the save itself lands moments later.
     */
    exportCompaniesWithSave: builder.mutation<
      { status?: string },
      {
        firstname: string;
        lastname: string;
        primary_email: string;
        companyIds: string[];
      }
    >({
      query: (body) => ({
        url: "/export-with-save",
        method: "POST",
        body,
      }),
      transformResponse: (response: Envelope<{ status?: string }>) => response?.data ?? {},
    }),
  }),
});

export const {
  useSaveCompaniesMutation,
  useDeleteSavedCompaniesMutation,
  useSearchSavedCompaniesQuery,
  useLazySearchSavedCompaniesQuery,
  useLazySearchSavedCompaniesSelectionQuery,
  useExportSavedCompaniesToCrmMutation,
  useExportCompaniesWithSaveMutation,
} = savedCompaniesApi;

export default savedCompaniesApi;
export { EMPTY_SEARCH as EMPTY_SAVED_COMPANIES_SEARCH };
